import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BASE_URL = 'https://www.clouds-art.com';
const SUPPORTED_LANGS = ['en', 'fr', 'ar'];

interface SeoHeadProps {
  title: string;
  description: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  canonicalPath?: string;
  schemas?: Record<string, any>[];
  noIndex?: boolean;
}

/**
 * SeoHead — Reusable SEO component.
 * Automatically injects:
 * - <title>
 * - <meta name="description">
 * - <link rel="canonical">
 * - <link rel="alternate" hreflang="..."> for each language (en, fr, ar, x-default)
 * - Open Graph & Twitter meta tags
 * - Schema.org JSON-LD (Organization by default + custom schemas)
 */
export default function SeoHead({
  title,
  description,
  ogImage,
  ogType = 'website',
  canonicalPath,
  schemas,
  noIndex = false
}: SeoHeadProps) {
  const { i18n } = useTranslation();
  const location = useLocation();

  // Strip the lang prefix from current path to get the clean page path
  const pathParts = location.pathname.split('/').filter(Boolean);
  const detectedPagePath = SUPPORTED_LANGS.includes(pathParts[0])
    ? '/' + pathParts.slice(1).join('/')
    : location.pathname;

  const pagePath = canonicalPath || (detectedPagePath === '/' ? '' : detectedPagePath);
  const currentLang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : 'en';

  const canonicalUrl = `${BASE_URL}/${currentLang}${pagePath}`;
  const image = ogImage
    ? (ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`)
    : `${BASE_URL}/images/hero%202.webp`;

  const defaultOrgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Clouds Art",
    "alternateName": "Pending Clouds Art",
    "url": BASE_URL,
    "logo": `${BASE_URL}/favicon.svg`,
    "description": "B2B manufacturer of custom 3D wall art, tactile reliefs, and bespoke art installations for luxury hospitality and corporate spaces.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Shenzhen",
      "addressCountry": "CN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service & B2B inquiries",
      "email": "contact@clouds-art.com",
      "areaServed": "Global"
    }
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* SEO-02: Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* SEO-03: hreflang alternates */}
      {SUPPORTED_LANGS.map((lang) => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={`${BASE_URL}/${lang}${pagePath}`}
        />
      ))}
      {/* x-default points to English */}
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/en${pagePath}`} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Clouds Art" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {noIndex && <meta name="robots" content="noindex, follow" />}

      {/* Base Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify(defaultOrgSchema)}
      </script>

      {/* Additional Page Schemas */}
      {schemas?.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
